/**
 * LLM 上下文截断设置区块（附件截断 / 知识库 / 温度 / 保存）
 */
import React from 'react';
import { SystemModelConfig } from '../types';
import NumberInput from './NumberInput';

interface TruncationSettingsSectionProps {
  modelConfig: SystemModelConfig;
  setModelConfig: (cfg: SystemModelConfig) => void;
  configSaving: boolean;
  handleSaveConfig: () => void;
}

export default function TruncationSettingsSection({
  modelConfig, setModelConfig, configSaving, handleSaveConfig,
}: TruncationSettingsSectionProps) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-gray-900 mb-4">📄 LLM 上下文截断设置</h3>
      <p className="text-xs text-gray-500 mb-4">
        控制附件提取文本送入 LLM 时的字符截断上限（企业附件 / 项目附件），以及知识库检索参数。
        注意：「知识库上下文上限」仅作用于知识库检索上下文，不控制附件截断；附件请用上方「企业附件截断 / 项目附件截断」。修改后即时生效。
      </p>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="企业附件截断 (字符数)"
            value={modelConfig.company_att_text_limit ?? 1500}
            onChange={(v) => setModelConfig({ ...modelConfig, company_att_text_limit: v })}
            min={100}
            max={200000}
          />
          <NumberInput
            label="项目附件截断 (字符数)"
            value={modelConfig.project_att_text_limit ?? 2000}
            onChange={(v) => setModelConfig({ ...modelConfig, project_att_text_limit: v })}
            min={100}
            max={200000}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <NumberInput
            label="知识库最大分块数"
            value={modelConfig.kb_max_chunks ?? 6}
            onChange={(v) => setModelConfig({ ...modelConfig, kb_max_chunks: v })}
            min={1}
            max={50}
          />
          <NumberInput
            label="知识库上下文上限 (字符数)"
            value={modelConfig.kb_max_total_chars ?? 4000}
            onChange={(v) => setModelConfig({ ...modelConfig, kb_max_total_chars: v })}
            min={500}
            max={200000}
            hint="仅作用于知识库检索上下文，不控制附件截断（附件请用上方企业/项目附件截断）"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              知识库检索模式
            </label>
            <select
              value={modelConfig.kb_retrieval_mode ?? 'keyword'}
              onChange={(e) => setModelConfig({ ...modelConfig, kb_retrieval_mode: e.target.value as 'keyword' | 'vector' | 'hybrid' })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="keyword">关键词匹配</option>
              <option value="vector">语义检索</option>
              <option value="hybrid">混合模式</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              关键词匹配基于 Bigram 精确匹配；语义检索基于向量相似度；混合模式融合两者结果（RRF 排序）
            </p>
          </div>
          <div />
        </div>
        <button
          onClick={handleSaveConfig}
          disabled={configSaving}
          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          {configSaving ? '保存中...' : '保存截断设置'}
        </button>
      </div>
    </section>
  );
}
